import { connect } from "react-redux";

import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import { ExternalBindingPath } from "../../../Engine/Core/CoreTypes";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { IHelperFunctionsCallContext } from "../../../Engine/Form/CustomizationContexts/HelperFunctionContext/IHelperFunctionsCallContext";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { AbsoluteResolvedModelPath, createAbsolute } from "../../../../../Common/ModelPath/ModelPath";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { withFormObject } from "../../withFormObject";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { withControlDebugInfo } from "../../withControlDebugInfo";

import { ContractorsQueryResult, ContractorsSuggestComboBox } from "./ContractorsSuggestComboBox";

interface ContractorsSuggestComboBoxContainerProps extends FormActionHandlerExecutionInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    disabled2?: EvaluableProp<boolean>;
    viewModelSelect: ViewModelSelect;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    query: (
        callContext: IHelperFunctionsCallContext,
        search: string | null,
        options: { bindingPath: ExternalBindingPath }
    ) => Promise<ContractorsQueryResult>;
    onUnexpectedInput?: FormActionHandler;
}

const mapStateToProps = (state: FullFormState, ownProps: ContractorsSuggestComboBoxContainerProps) => {
    const { bindingPath, viewModelSelect, disabled2, processEvaluableProp } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);

    return {
        value: { displayValue: controlModel.value ?? "", payload: {} },
        disabled: processEvaluableProp(state, disabled2) || controlModel.disabled,
        error: controlModel.error,
        warning: controlModel.warning,
        isReadOnly: state.settings.isReadOnly,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: ContractorsSuggestComboBoxContainerProps) => {
    const { bindingPath } = ownProps;

    return {
        onChange: (value: undefined | null | string) => dispatch(setToModel(bindingPath, "value", value)),
        setInfo: (path: ExternalBindingPath, value: string) =>
            dispatch(setToModel(createAbsolute(path), "value", value)),
        onUnexpectedInput: async (query: string) => {
            if (ownProps.onUnexpectedInput != undefined) {
                await ownProps.executeFormActionHandler(ownProps.onUnexpectedInput, query);
            }
        },
        onQuery: (search: string | null) =>
            dispatch(async (_, __, form) =>
                ownProps.query(form.getHelperFunctionsCallContext(), search, {
                    bindingPath: ownProps.bindingPath.toLegacyPath(),
                })
            ),
    };
};

export const ContractorsSuggestComboBoxContainer = withResolvePaths(
    withViewModelSelect(
        withFormActionHandlerExecution(
            withEvaluators(
                connect(
                    mapStateToProps,
                    mapDispatchToProps
                )(
                    withFocusManagement(
                        withExternalMountingEventReporting(
                            withFormObject(withControlDebugInfo(ContractorsSuggestComboBox))
                        )
                    )
                )
            )
        )
    )
);
